import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  Link,
  TextField,
  Typography,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import {
  FiAlertCircle,
  FiCamera,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiShield,
  FiUpload,
  FiUser,
} from 'react-icons/fi';

import { uploadPassportRequest, uploadSelfieRequest } from '../../../../api/storage';
import { triggerPassportCheckByUser } from '../../../../api/checkers';
import { triggerRTWCheckByUser } from '../../../../api/users';
import { getPassportRequest } from '../../../../api/documents';
import { ChatCard, ChatCardSection } from '../../../../components/Chat/kit';
import { formatDate } from '../../../../utils/dates';
import { LailaCardsContext } from './LailaCardsContext';
import type { LailaCardViewProps } from './LailaCard';
import {
  GET_SHARE_CODE_URL,
  MyChecksData,
  canSendId,
  checksToDo,
  formatShareCode,
  idDone,
  idWaiting,
  normaliseShareCode,
  rtwDone,
  rtwErrorMessage,
  rtwProblem,
  rtwWaiting,
} from './myChecks';

type PhotoKind = 'document' | 'selfie';

const longDate = (iso: string) => formatDate(iso, 'D MMM YYYY');

// The worker's own ID and Right to Work checks (the my_checks card): they pick
// the passport or ID photo and the selfie, or type their share code, and send
// them from here with the page's own calls, as Profile → ID & RTW does. Photos
// stay in the browser until sent and never reach Laila.
export const MyChecksCard: React.FC<LailaCardViewProps> = ({ card }) => {
  const classes = useStyles();
  const { updateCard, noteForLaila } = useContext(LailaCardsContext);
  const data = card.data as unknown as MyChecksData;

  // New photos as files: card data is kept with the chat, a file isn't.
  const [photos, setPhotos] = useState<{ document?: File; selfie?: File }>({});
  const [shareCode, setShareCode] = useState(formatShareCode(data.rtw.shareCode));
  const [dateOfBirth, setDateOfBirth] = useState(data.rtw.dateOfBirth);
  const [touched, setTouched] = useState(false);
  const inputs = { document: useRef<HTMLInputElement>(null), selfie: useRef<HTMLInputElement>(null) };

  const previews = useMemo(
    () => ({
      document: photos.document && URL.createObjectURL(photos.document),
      selfie: photos.selfie && URL.createObjectURL(photos.selfie),
    }),
    [photos]
  );
  useEffect(() => () => Object.values(previews).forEach(url => url && URL.revokeObjectURL(url)), [previews]);

  const save = (next: MyChecksData) => updateCard(card.id, next as unknown as Record<string, unknown>);
  const saveId = (id: Partial<MyChecksData['id']>) => save({ ...data, id: { ...data.id, ...id } });
  const saveRtw = (rtw: Partial<MyChecksData['rtw']>) => save({ ...data, rtw: { ...data.rtw, ...rtw } });

  const pick = (kind: PhotoKind) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) {
      setPhotos(current => ({ ...current, [kind]: file }));
    }
  };

  const sendId = async () => {
    if (!canSendId(data, photos)) {
      return;
    }
    saveId({ state: 'sending', message: undefined });
    let uploaded: { passport: string; face: string };
    try {
      // A photo not replaced is the one on file, as the profile page sends it.
      const onFile = photos.document && photos.selfie ? null : (await getPassportRequest()).data;
      uploaded = {
        passport: photos.document
          ? (await uploadPassportRequest(photos.document)).data.url
          : onFile?.document?.url || '',
        face: photos.selfie
          ? (await uploadSelfieRequest(photos.selfie)).data.url
          : onFile?.selfie?.url || '',
      };
    } catch (e) {
      saveId({
        state: 'failed',
        message: 'We couldn’t upload your photos. Check your connection and try again.',
      });
      return;
    }
    let alreadyRunning = false;
    try {
      // The gateway takes the user from the token; userId is unused.
      await triggerPassportCheckByUser({
        userId: '',
        payload: { passport_url: uploaded.passport, face_url: uploaded.face },
      });
    } catch (e: any) {
      if (e?.response?.status !== 429) {
        saveId({
          state: 'failed',
          message: 'We couldn’t start the check. Please try again in a few minutes.',
        });
        return;
      }
      alreadyRunning = true;
    }
    setPhotos({});
    saveId({ state: 'started', message: undefined, hasDocument: true, hasSelfie: true });
    noteForLaila(
      alreadyRunning
        ? 'The user sent new ID photos from the card, but a check of their earlier photos was still running, so the new ones are not part of it.'
        : 'The user sent their passport or ID photo and selfie from the card. The ID check is running and usually takes a few minutes.'
    );
  };

  const problem = rtwProblem(shareCode, dateOfBirth);
  const sendRtw = async () => {
    setTouched(true);
    if (problem || data.rtw.state === 'sending') {
      return;
    }
    const code = normaliseShareCode(shareCode);
    saveRtw({ state: 'sending', message: undefined, shareCode: code, dateOfBirth });
    try {
      await triggerRTWCheckByUser({ payload: { rtw_share_code: code, date_of_birth: dateOfBirth } });
      saveRtw({ state: 'started', shareCode: code, dateOfBirth });
      noteForLaila('The user sent their share code from the card. The Right to Work check is running.');
    } catch (e) {
      saveRtw({ state: 'failed', message: rtwErrorMessage(e), shareCode: code, dateOfBirth });
    }
  };

  const status = (icon: React.ReactNode, text: string) => (
    <Box className={classes.status}>
      {icon}
      <Typography variant="body2">{text}</Typography>
    </Box>
  );

  const photoRow = (kind: PhotoKind, title: string, onFile: boolean, hint: string) => {
    const picked = photos[kind];
    const isDocument = kind === 'document';
    return (
      <Box className={classes.photoRow}>
        {picked ? (
          <img src={previews[kind]} alt="" className={classes.thumb} />
        ) : (
          <Box className={`${classes.thumb} ${classes.thumbEmpty}`} aria-hidden>
            {onFile ? <FiCheck className={classes.success} /> : isDocument ? <FiCreditCard /> : <FiUser />}
          </Box>
        )}
        <Box className={classes.grow}>
          <Typography variant="body2">{title}</Typography>
          <Typography variant="caption" color={picked || onFile ? 'textSecondary' : 'error'} component="p">
            {picked ? 'New photo, not sent yet' : onFile ? 'On file' : 'Needed'}
          </Typography>
          <Typography variant="caption" color="textSecondary" component="p">
            {hint}
          </Typography>
          {/* On a phone, capture opens the camera: the back one for the document, the front for a selfie. */}
          <input
            ref={inputs[kind]}
            type="file"
            accept="image/*"
            capture={isDocument ? 'environment' : 'user'}
            hidden
            aria-label={title}
            onChange={pick(kind)}
          />
          <Button
            size="small"
            color="primary"
            variant="outlined"
            className={classes.pick}
            startIcon={isDocument ? <FiUpload aria-hidden /> : <FiCamera aria-hidden />}
            onClick={() => inputs[kind].current?.click()}
          >
            {picked || onFile ? 'Replace photo' : isDocument ? 'Add photo' : 'Add selfie'}
          </Button>
        </Box>
      </Box>
    );
  };

  const idSection = () => {
    if (idDone(data)) {
      return status(
        <FiCheckCircle className={classes.success} aria-hidden />,
        data.id.expires ? `Verified · valid until ${longDate(data.id.expires)}` : 'Verified'
      );
    }
    if (idWaiting(data)) {
      return status(
        <FiClock className={classes.brand} aria-hidden />,
        'We’re checking your photos. It usually takes a few minutes, and we’ll let you know if anything’s wrong.'
      );
    }
    return (
      <>
        {data.id.status === 'declined' && (
          <Typography variant="caption" color="error">
            Your last photos didn’t pass the check. Please add new ones.
          </Typography>
        )}
        {data.id.status === 'expired' && (
          <Typography variant="caption" color="error">
            The document on file has expired. Please add one that’s in date.
          </Typography>
        )}
        {photoRow(
          'document',
          'Passport or ID card',
          data.id.hasDocument,
          'The photo page, all of it, with no glare. A driving licence can’t be used.'
        )}
        {photoRow('selfie', 'Selfie', data.id.hasSelfie, 'Your face, straight on, with no hat or glasses.')}
        {data.id.state === 'failed' && !!data.id.message && (
          <Box className={classes.alert} role="alert">
            <FiAlertCircle aria-hidden />
            <Typography variant="body2">{data.id.message}</Typography>
          </Box>
        )}
        <Box>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            startIcon={<FiShield aria-hidden />}
            disabled={!canSendId(data, photos)}
            onClick={sendId}
          >
            {data.id.state === 'sending'
              ? 'Sending…'
              : data.id.state === 'failed'
              ? 'Try again'
              : 'Send for checking'}
          </Button>
        </Box>
      </>
    );
  };

  const rtwSection = () => {
    if (!data.rtw.applies) {
      return status(
        <FiCheckCircle className={classes.success} aria-hidden />,
        'British and Irish citizens don’t need a share code: your passport covers it.'
      );
    }
    if (rtwDone(data)) {
      return status(
        <FiCheckCircle className={classes.success} aria-hidden />,
        data.rtw.expires ? `Verified · valid until ${longDate(data.rtw.expires)}` : 'Verified'
      );
    }
    if (rtwWaiting(data)) {
      return status(
        <FiClock className={classes.brand} aria-hidden />,
        'We’re checking your share code with the Home Office. We’ll let you know when it’s done.'
      );
    }
    const message = touched && problem ? problem : data.rtw.state === 'failed' ? data.rtw.message : '';
    return (
      <>
        {data.rtw.status === 'no_rtw' && (
          <Typography variant="caption" color="error">
            Your last share code didn’t confirm a right to work. Get a new one and try again.
          </Typography>
        )}
        <Box className={classes.fields}>
          <TextField
            id={`${card.id}-share-code`}
            label="Share code"
            placeholder="W12 345 678"
            size="small"
            variant="outlined"
            value={shareCode}
            onChange={event => setShareCode(formatShareCode(event.target.value))}
            inputProps={{ maxLength: 11, autoCapitalize: 'characters', spellCheck: false }}
          />
          <TextField
            id={`${card.id}-date-of-birth`}
            label="Date of birth"
            type="date"
            size="small"
            variant="outlined"
            value={dateOfBirth}
            onChange={event => setDateOfBirth(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        {!!message && (
          <Box className={classes.alert} role="alert">
            <FiAlertCircle aria-hidden />
            <Typography variant="body2">{message}</Typography>
          </Box>
        )}
        <Box className={classes.buttons}>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            startIcon={<FiCheck aria-hidden />}
            disabled={data.rtw.state === 'sending'}
            onClick={sendRtw}
          >
            {data.rtw.state === 'sending'
              ? 'Sending…'
              : data.rtw.state === 'failed'
              ? 'Try again'
              : 'Check my share code'}
          </Button>
          <Link href={GET_SHARE_CODE_URL} target="_blank" rel="noopener noreferrer" variant="body2">
            No share code? Get one on GOV.UK
          </Link>
        </Box>
      </>
    );
  };

  const toDo = checksToDo(data);
  // What they asked about comes first.
  const sections = [
    <ChatCardSection key="id" title="ID check">
      {idSection()}
    </ChatCardSection>,
    <ChatCardSection key="rtw" title="Right to Work">
      {rtwSection()}
    </ChatCardSection>,
  ];

  return (
    <ChatCard
      label="Your checks"
      icon={<FiShield aria-hidden />}
      iconTone={toDo ? 'brand' : 'success'}
      title="Your ID and Right to Work"
      badge={toDo ? `${toDo} to do` : 'All done'}
      badgeTone={toDo ? 'warning' : 'success'}
    >
      {data.focus === 'rtw' ? sections.reverse() : sections}
    </ChatCard>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    grow: { flex: 1, minWidth: 0 },
    status: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      '& svg': { flex: 'none', marginTop: 3 },
    },
    success: { color: theme.palette.success.main },
    brand: { color: theme.palette.primary.main },
    photoRow: { display: 'flex', gap: 12, padding: '6px 0' },
    thumb: {
      width: 56,
      height: 56,
      flex: 'none',
      borderRadius: 10,
      objectFit: 'cover',
      border: `1px solid ${theme.palette.divider}`,
    },
    thumbEmpty: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.palette.text.secondary,
      fontSize: 20,
    },
    pick: { marginTop: 6 },
    fields: { display: 'flex', flexWrap: 'wrap', gap: 8, '& > *': { flex: '1 1 160px' } },
    buttons: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 },
    alert: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      color: theme.palette.error.main,
      '& svg': { flex: 'none', marginTop: 3 },
    },
  })
);
